"use client";

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";
import { Calendar, Check, Sparkles, Upload, Users } from "lucide-react";

import { Dialog, DialogBody, DialogCloseButton, DialogHeader } from "@speakai/ui/dialog";
import { useActivationStatus, type ActivationStepId } from "@/hooks/use-activation-status";
import { useAppStore } from "@/stores";
import { useOpenCapture } from "@/hooks/use-open-capture";
import { cn } from "@/lib/utils/cn";
import { track, trackFunnel } from "@/lib/integrations/sentry-tracking";
import { pushDataLayer } from "@/lib/integrations/ga4";

interface PowerspotPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional — when provided, popover footer renders a "Dismiss" link that calls this. */
  onDismiss?: () => void;
}

interface StepDef {
  id: ActivationStepId;
  done: boolean;
  icon: LucideIcon;
  labelKey: string;
  defaultLabel: string;
  action: () => void;
}

export function PowerspotPopover({ open, onOpenChange, onDismiss }: PowerspotPopoverProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const openCapture = useOpenCapture();
  const status = useActivationStatus();
  const openPanelFromHeader = useAppStore((state) => state.openPanelFromHeader);

  useEffect(() => {
    if (!open) return;
    trackFunnel("activation", "topbar_activation_opened", {
      goal_bucket: status.goalBucket,
      completed_count: status.completedCount,
      total_steps: status.totalSteps,
    });
    pushDataLayer("Activation", "Topbar Activation Opened", "");
  }, [open, status.goalBucket, status.completedCount, status.totalSteps]);

  const stepsById: Record<ActivationStepId, StepDef> = {
    upload: {
      id: "upload",
      done: status.hasMedia,
      icon: Upload,
      labelKey: "DASHBOARD.ACTIVATION.UPLOAD_TITLE",
      defaultLabel: "Upload a file",
      action: () => openCapture("upload"),
    },
    chat: {
      id: "chat",
      done: status.hasUsedChat,
      icon: Sparkles,
      labelKey: "DASHBOARD.ACTIVATION.CHAT_TITLE",
      defaultLabel: "Ask Magic Chat a question",
      action: () => openPanelFromHeader(),
    },
    calendar: {
      id: "calendar",
      done: status.hasCalendar,
      icon: Calendar,
      labelKey: "DASHBOARD.ACTIVATION.CALENDAR_TITLE",
      defaultLabel: "Connect your calendar",
      action: () => navigate("/meeting-assistant"),
    },
    team: {
      id: "team",
      done: status.hasTeammate,
      icon: Users,
      labelKey: "DASHBOARD.ACTIVATION.TEAM_TITLE",
      defaultLabel: "Invite a teammate",
      action: () => navigate("/team"),
    },
  };

  const orderedSteps = status.orderedStepIds.map((id, position) => ({
    position,
    ...stepsById[id],
  }));

  function handleStepClick(step: typeof orderedSteps[number]) {
    if (step.done) return;
    trackFunnel("activation", "step_clicked", {
      step_id: step.id,
      position: step.position,
      goal_bucket: status.goalBucket,
      has_goal: status.onboardingGoal !== null,
      surface: "topbar_popover",
    });
    track("activation", "topbar_popover_step_clicked", { step_id: step.id });
    pushDataLayer("Activation", "Step Clicked", `${step.id}:${status.goalBucket}`);
    onOpenChange(false);
    step.action();
  }

  return (
    <Dialog
      id="powerspot-activation-popover"
      open={open}
      onClose={() => onOpenChange(false)}
      size="sm"
      data-testid="powerspot-popover-root"
      aria-labelledby="powerspot-activation-title"
    >
      <DialogHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col">
            <h2
              id="powerspot-activation-title"
              className="text-base font-semibold text-foreground"
            >
              {t("TOPBAR.POWERSPOT.ACTIVATION.POPOVER_TITLE", {
                defaultValue: "Set up your account",
              })}
            </h2>
            <p className="text-xs text-muted-foreground">
              {t("TOPBAR.POWERSPOT.ACTIVATION.POPOVER_SUBTITLE", {
                completed: status.completedCount,
                total: status.totalSteps,
                defaultValue: "{{completed}} of {{total}} steps done",
              })}
            </p>
          </div>
          <DialogCloseButton onClose={() => onOpenChange(false)} />
        </div>
      </DialogHeader>
      <DialogBody>
        <ul className="flex flex-col gap-2">
          {orderedSteps.map((step) => {
            const Icon = step.done ? Check : step.icon;
            return (
              <li key={step.id}>
                <button
                  type="button"
                  onClick={() => handleStepClick(step)}
                  disabled={step.done}
                  title={
                    step.done
                      ? t("TOPBAR.POWERSPOT.ACTIVATION.STEP_COMPLETED_TITLE", "Step completed")
                      : undefined
                  }
                  className={cn(
                    "flex w-full items-center gap-3 rounded-md border border-border px-3 py-2 text-left text-sm transition-colors",
                    step.done
                      ? "cursor-default opacity-60 text-muted-foreground"
                      : "text-foreground hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                  )}
                  data-testid={`powerspot-popover-step-${step.id}`}
                  aria-disabled={step.done}
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="flex-1">{t(step.labelKey, { defaultValue: step.defaultLabel })}</span>
                </button>
              </li>
            );
          })}
        </ul>
        {onDismiss && (
          <div className="mt-4 flex justify-center border-t border-border pt-3">
            <button
              type="button"
              onClick={onDismiss}
              className="text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:underline"
              data-testid="powerspot-popover-dismiss"
            >
              {t("TOPBAR.POWERSPOT.ACTIVATION.POPOVER_DISMISS", {
                defaultValue: "Dismiss for 7 days",
              })}
            </button>
          </div>
        )}
      </DialogBody>
    </Dialog>
  );
}
